import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  CREATE_REFERRAL_LINK,
  createReferralLink,
  GET_REFERRAL_LIST_ACTION,
  GET_REFERRAL_PAYOUTS_ACTION,
  GET_REFERRAL_SUMMARY_ACTION,
  getReferralPayouts,
} from '~/actions';
import { Box, CardSpinner, Center, Column, Pressable, Row, Text } from '~/commonComponents';
import { IReferrer, useGetReferrer } from '~/domain/Referral';
import { useGetSelectedOffice } from '~/domain/SelectedOffice';
import { getShouldDisableEmployeeReferralStates, shouldUseReferralProgramV2 } from '~/growthbook';
import { useLoading, useUserSelector } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IReferral } from '~/models';
import { routes } from '~/routes';
import { Colors } from '~/themes/colors';
import { GotReferredSection } from './components/GotReferredSection';
import { InputReferralLink } from './components/GotReferredSection/components/InputReferralLink/InputReferralLink';
import { InstructionItem } from './components/InstructionItem';
import { ReferralLink } from './components/ReferralLink';
import { ReferralLinkModals } from './components/ReferralLinkModals';
import { ReferralList } from './components/ReferralList';
import { ReferralSummary } from './components/ReferralSummary';
import { useApplyReferralLink } from './hooks/useApplyReferralLink';
import { useReferralList } from './hooks/useReferralList';

import ReferralGiftCard from '~/images/referral/referral-gift-card.svg';
import GiftIcon from '~/images/referral/referral-gift-icon.svg';
import PeopleIcon from '~/images/referral/referral-people-icon.svg';
import ShareIcon from '~/images/referral/referral-share-icon.svg';

const loadingActions = [
  CREATE_REFERRAL_LINK,
  GET_REFERRAL_LIST_ACTION,
  GET_REFERRAL_SUMMARY_ACTION,
  GET_REFERRAL_PAYOUTS_ACTION,
];

export const ReferralScreenV2 = () => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const mutateGetSelectedOffice = useGetSelectedOffice(user?.id);
  const { isLoading } = useLoading(loadingActions);
  const { totalUserReferred, totalEarned, referralList, fetchReferralList } = useReferralList();
  const { data: referrer, isLoading: isReferrerLoading } = useGetReferrer();

  const {
    shortLink,
    showPopup,
    isLoading: isLoadingApply,
    handleClosePopup,
    isSuccess: isLinkingSuccess,
    submitReferralLink,
    errorMessage,
  } = useApplyReferralLink();

  const referralProgramDisabledStates = getShouldDisableEmployeeReferralStates();
  const isReferralProgramEnabledForUserState = !referralProgramDisabledStates?.includes(
    user.address?.[0]?.state,
  );
  const isGotReferredSectionEnabled =
    shouldUseReferralProgramV2() &&
    isReferralProgramEnabledForUserState &&
    !user.referralId &&
    !user?.completedShiftCount;

  useEffect(() => {
    mutateGetSelectedOffice.mutateAsync();
    dispatch(createReferralLink());
    dispatch(getReferralPayouts());
    fetchReferralList();
  }, [dispatch]);

  if (isLoading || isReferrerLoading) {
    return <CardSpinner />;
  }

  if (isGotReferredSectionEnabled) {
    return (
      <Column flex={1}>
        <Box className="component_container" flexDirection="initial">
          <Box className="floating_container" padding={40}>
            <Center paddingBottom={40}>
              <Text bold textAlign="center" fontSize={32} marginTop={32}>
                Referral Program
              </Text>

              <img
                src={ReferralGiftCard}
                style={{ width: 203, height: 169, paddingTop: 32 }}
                alt="icon"
              />

              <Center marginTop={8}>
                <GotReferredSection />
                <InputReferralLink
                  shortLink={shortLink}
                  isLinkingSuccess={isLinkingSuccess}
                  isLoading={isLoadingApply}
                  onSubmit={submitReferralLink}
                  errorMessage={errorMessage}
                />
              </Center>
            </Center>
          </Box>
        </Box>

        <Box className="component_container" flexDirection="initial">
          <Box className="floating_container" padding={40} marginTop={-12}>
            <Center>
              <InviteFriends
                totalUserReferred={totalUserReferred}
                totalEarned={totalEarned}
                referralList={referralList}
                referrer={referrer}
              />
            </Center>
          </Box>
        </Box>

        <ReferralLinkModals
          showPopup={showPopup}
          onClosePopup={handleClosePopup}
        />
      </Column>
    );
  }

  return (
    <>
      <Box className="component_container" flexDirection="initial">
        <Box className="floating_container" padding={40}>
          <Center>
            <Header />

            <InviteFriends
              totalUserReferred={totalUserReferred}
              totalEarned={totalEarned}
              referralList={referralList}
              referrer={referrer}
            />
          </Center>
        </Box>
      </Box>

      <ReferralLinkModals
        showPopup={showPopup}
        onClosePopup={handleClosePopup}
      />
    </>
  );
};

const Header = () => {
  return (
    <Center>
      <Text bold textAlign="center" fontSize={32} marginTop={32}>
        Referral Program
      </Text>

      <img src={ReferralGiftCard} style={{ width: 203, height: 169, paddingTop: 32 }} alt="icon" />
    </Center>
  );
};

const InviteFriends = ({
  totalUserReferred,
  totalEarned,
  referralList,
  referrer,
}: {
  totalUserReferred: number | null;
  totalEarned: number | null;
  referralList: IReferral[];
  referrer: IReferrer | null;
}) => {
  const navigate = useNavigate();
  const { referralLink } = useAppSelector((state) => state.user);

  return (
    <>
      <Text color="primary_500" bold fontSize={22} paddingTop={32}>
        Invite Friends,{' '}
        <Text color="success_400" bold fontSize={22}>
          Get an E-Gift Card!
        </Text>
      </Text>

      {shouldUseReferralProgramV2() && (
        <Box marginBottom={24}>
          <ReferralLink link={referralLink || ''} style={{ marginTop: 32 }} />
        </Box>
      )}

      <Row gap={32} paddingTop={26} width={960} justifyContent="space-between">
        <InstructionItem
          icon={ShareIcon}
          title="1. Share Your Code"
          description={
            shouldUseReferralProgramV2() ? (
              <>
                Send your link to as many friends or
                <br />
                colleagues as you like!
              </>
            ) : (
              <>
                Send your link to as many friends or
                <br />
                offices as you like!
              </>
            )
          }
        />

        <InstructionItem
          icon={PeopleIcon}
          title="2. Your Friend Completes a Shift"
          description={
            <>
              Once your friend completes their first shift
              <br />
              successfully, we’ll notify you both.
            </>
          }
        />

        <InstructionItem
          icon={GiftIcon}
          title="3. You Both Get Rewards!"
          description={
            shouldUseReferralProgramV2() ? (
              <>
                You’ll receive your gift card via
                <br />
                your associated email.
              </>
            ) : (
              <>
                That’s it! You’ll both receive a<br />
                referral reward!
              </>
            )
          }
        />
      </Row>

      <Box height={1} width="70%" backgroundColor={Colors.neutral_100} marginTop={40} />

      {referralList?.length > 0 ? (
        <ReferralSummary
          totalReferrals={totalUserReferred || 0}
          totalEarned={totalEarned || 0}
          style={{ marginTop: 32 }}
        />
      ) : null}

      {!shouldUseReferralProgramV2() && (
        <ReferralLink link={referralLink || ''} style={{ marginTop: 32 }} />
      )}

      <ReferralList referrer={referrer} data={referralList} />

      <Pressable onPress={() => navigate(routes.referralDisclaimer)} style={{ paddingBlock: 40 }}>
        <Text bold color="secondary_500">
          Disclaimer/ Terms and Conditions
        </Text>
      </Pressable>
    </>
  );
};
